import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Copy, Edit, Share2, AlertTriangle, ExternalLink } from "lucide-react";
import { fetchDeck, type Deck } from "@/lib/api/deck-api";
import { Deck as DeckClass } from "@/lib/classes/deck";
import { DeckPreview } from "@/components/DeckDrafter/DeckPreview";
import { useDataStore } from "@/lib/store/data-store";
import { useAuth } from "@/lib/store/auth-store";
import { copyDeckCodeToClipboard, getDeckShareUrl, copyDeckShareUrl } from "@/lib/utils/deck-library";
import { getDivisionImagePath, getCountryFlagPath } from "@/lib/utils/division-images";
import { toast } from "sonner";

export function DeckDetail() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const { units, divisions, loadingUnits, loadingDivisions } = useDataStore();
  const { user } = useAuth();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [deckInstance, setDeckInstance] = useState<DeckClass | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const loadDeck = async () => {
      if (!deckId) {
        setError("Deck ID is required");
        setLoading(false);
        return;
      }

      const id = parseInt(deckId, 10);
      if (isNaN(id)) {
        setError("Invalid deck ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const loadedDeck = await fetchDeck(id);
        setDeck(loadedDeck);

        // Parse deck code to create deck instance
        if (units && divisions && divisions.length > 0) {
          const unitMap: Record<string, typeof units[0]> = {};
          for (const unit of units) {
            unitMap[unit.descriptorName] = unit;
          }

          try {
            const parsedDeck = DeckClass.fromDeckCode(loadedDeck.deck_code, {
              unitMap,
              divisions,
            });
            setDeckInstance(parsedDeck);
          } catch (parseError) {
            console.error("Failed to parse deck code:", parseError);
            setError("Failed to parse deck code. The deck may be outdated.");
          }
        }
      } catch (err) {
        console.error("Failed to load deck:", err);
        setError(err instanceof Error ? err.message : "Failed to load deck");
      } finally {
        setLoading(false);
      }
    };

    if (!loadingUnits && !loadingDivisions) {
      loadDeck();
    }
  }, [deckId, units, divisions, loadingUnits, loadingDivisions]);

  const handleCopyCode = async () => {
    if (!deck) return;
    try {
      setCopying(true);
      await copyDeckCodeToClipboard(deck.deck_code);
      toast.success("Deck code copied to clipboard");
    } catch (error) {
      console.error("Failed to copy deck code:", error);
      toast.error("Failed to copy deck code to clipboard");
    } finally {
      setCopying(false);
    }
  };

  const handleShare = async () => {
    if (!deck) return;
    try {
      setSharing(true);
      await copyDeckShareUrl(deck.deck_code);
      toast.success("Share link copied to clipboard");
    } catch (error) {
      console.error("Failed to copy share link:", error);
      toast.error("Failed to copy share link");
    } finally {
      setSharing(false);
    }
  };

  const handleEdit = () => {
    if (!deck) return;
    navigate(`/deck-builder?code=${encodeURIComponent(deck.deck_code)}&deckId=${deck.id}&edit=true`);
  };

  const isOwner = user?.discordId === deck?.discord_id;

  if (loadingUnits || loadingDivisions || loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-4">
              <div className="text-center">
                <p className="text-destructive mb-2 text-sm">{error || "Deck not found"}</p>
                <Button onClick={() => navigate("/deck-library")} variant="outline" size="sm">
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Back to Deck Library
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const division = divisions?.find(d => d.descriptor === deck.division_descriptor);
  const deckTags = deck.tags
    ? deck.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
    : [];

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => navigate("/deck-library")}
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Library
          </Button>
        </div>

        {/* Deck Metadata */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                {division && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <img
                      src={getDivisionImagePath(division.descriptor)}
                      alt={division.name}
                      className="h-12 w-12 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <img
                      src={getCountryFlagPath(division.country)}
                      alt={division.country}
                      className="h-8 w-8 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <CardTitle className="text-2xl font-bold break-words">
                      {deck.name}
                    </CardTitle>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {deck.isOutdated && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Outdated
                        </Badge>
                      )}
                      {!deck.is_public && (
                        <Badge variant="secondary">Private</Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="break-all">{deck.author_name || deck.discord_username || 'Unknown'}</span>
                    <span className="mx-1">•</span>
                    <span className="whitespace-nowrap">
                      {new Date(deck.created_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </span>
                    {deck.updated_at !== deck.created_at && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="whitespace-nowrap">
                          Updated: {new Date(deck.updated_at).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </span>
                      </>
                    )}
                  </div>
                  {deckTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {deckTags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyCode}
                  disabled={copying}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copying ? "Copying..." : "Copy Code"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  disabled={sharing}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {sharing ? "Copying..." : "Share"}
                </Button>
                {isOwner && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleEdit}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Deck Preview */}
        {deckInstance ? (
          <Card>
            <CardHeader>
              <CardTitle>Deck Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <DeckPreview deck={deckInstance} />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <p>Unable to load deck preview. The deck code may be invalid or outdated.</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

